import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { useSiteSettings } from '../../contexts/SiteSettingsContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { Menu, X, ShoppingBag, ChevronDown, FileText, Globe, ExternalLink } from 'lucide-react';
import { categoriesApi } from '../../lib/api';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

// Animated Logo Component with CMS
const Logo = () => {
  const navigate = useNavigate();
  const { getSetting } = useSiteSettings();
  const logoUrl = getSetting('logo_dark', 'https://customer-assets.emergentagent.com/job_cd0460ea-ffc2-439c-bd21-54fcb026442d/artifacts/xso0m373_logo.png');
  const brandName = getSetting('brand_name', 'VAV WEAR');
  
  return (
    <motion.button 
      onClick={() => navigate('/')}
      className="flex items-center" 
      data-testid="logo-link"
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
    >
      <img 
        src={logoUrl} 
        alt={brandName} 
        className="h-10 w-auto"
      />
    </motion.button>
  );
};

// Animated Nav Link
const NavLink = ({ to, children, onClick, external = false }) => {
  const location = useLocation();
  const isActive = location.pathname === to || location.pathname.startsWith(to + '/');
  const navigate = useNavigate();
  
  const handleClick = (e) => {
    if (external) return; // Let browser handle external links
    e.preventDefault();
    if (onClick) onClick();
    setTimeout(() => navigate(to), 100);
  };

  if (external) {
    return (
      <a
        href={to}
        target="_blank"
        rel="noopener noreferrer"
        className="font-mono text-xs uppercase tracking-widest text-vav-fg-muted hover:text-vav-gold transition-colors duration-200 flex items-center gap-1"
      >
        {children}
        <ExternalLink className="w-3 h-3" />
      </a>
    );
  }
  
  return (
    <motion.a
      href={to}
      onClick={handleClick}
      className={`relative font-mono text-xs uppercase tracking-widest transition-colors duration-200 ${
        isActive ? 'text-vav-gold' : 'text-vav-fg-muted hover:text-vav-gold'
      }`}
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
    >
      {children}
      {isActive && (
        <motion.div
          className="absolute -bottom-1 left-0 right-0 h-0.5 bg-vav-gold"
          layoutId="activeNav"
        />
      )}
    </motion.a>
  );
};

// Language Switcher Component
const LanguageSwitcher = () => {
  const { language, toggleLanguage, t } = useLanguage();
  
  return (
    <button
      onClick={toggleLanguage}
      className="flex items-center gap-1.5 px-2 py-1 rounded border border-vav-border hover:border-vav-gold text-vav-fg-muted hover:text-vav-gold transition-colors"
      data-testid="language-switcher"
    >
      <Globe className="w-4 h-4" />
      <span className="font-mono text-xs uppercase">{language === 'tr' ? 'TR' : 'EN'}</span>
    </button>
  );
};

const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [catalogs, setCatalogs] = useState([]);
  const { user, isAuthenticated } = useAuth();
  const { getSetting } = useSiteSettings();
  const { language, t, getLocalizedField } = useLanguage();
  const navigate = useNavigate();

  // Fetch categories from API
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await categoriesApi.getAll();
        setCategories(response.data.categories || []);
      } catch (err) {
        console.error('Error fetching categories:', err);
      }
    };
    fetchCategories();
  }, []);

  // Fetch catalogs from API
  useEffect(() => {
    const fetchCatalogs = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/catalogs`);
        const data = await response.json();
        setCatalogs(data.catalogs || []);
      } catch (err) {
        console.error('Error fetching catalogs:', err);
      }
    };
    fetchCatalogs();
  }, []);

  // Get banner settings from CMS
  const bannerText = getSetting('banner_top', '');
  const bannerLink = getSetting('banner_top_link', '/shop');

  return (
    <>
      {/* Top Banner from CMS */}
      {bannerText && (
        <div className="fixed top-0 left-0 right-0 z-[60] bg-vav-gold text-black text-center py-2 text-sm font-medium">
          <a href={bannerLink} target="_blank" rel="noopener noreferrer" className="hover:underline">
            {bannerText}
          </a>
        </div>
      )}

      <motion.nav 
        className={`fixed ${bannerText ? 'top-10' : 'top-0'} left-0 right-0 z-50 bg-black/90 backdrop-blur-xl border-b border-vav-border`}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ type: 'spring', damping: 20 }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Logo />

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-6">
              {/* External Store Link */}
              <a 
                href="https://www.taktikstore.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="font-mono text-xs uppercase tracking-widest text-vav-fg-muted hover:text-vav-gold transition-colors duration-200"
              >
                {t('Mağaza', 'Store')}
              </a>
              
              {/* Categories Dropdown - Dynamic from Admin */}
              <DropdownMenu>
                <DropdownMenuTrigger className="flex items-center gap-1 font-mono text-xs uppercase tracking-widest text-vav-fg-muted hover:text-vav-gold transition-colors duration-200">
                  {t('Ürünler', 'Products')}
                  <ChevronDown className="w-3 h-3" />
                </DropdownMenuTrigger>
                <DropdownMenuContent className="bg-vav-bg-paper border-vav-border min-w-[200px]">
                  {categories.length > 0 ? (
                    categories.map((cat) => (
                      <DropdownMenuItem key={cat.category_id} asChild>
                        <a 
                          href={cat.external_url || `https://www.taktikstore.com/kategori/${cat.slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-vav-fg hover:text-vav-gold cursor-pointer flex items-center justify-between"
                        >
                          <span>{getLocalizedField(cat, 'name')}</span>
                          <ExternalLink className="w-3 h-3 opacity-50" />
                        </a>
                      </DropdownMenuItem>
                    ))
                  ) : (
                    <DropdownMenuItem disabled className="text-vav-fg-muted">
                      {t('Kategori yükleniyor...', 'Loading categories...')}
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Catalogs Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger className="flex items-center gap-1 font-mono text-xs uppercase tracking-widest text-vav-fg-muted hover:text-vav-gold transition-colors duration-200">
                  {t('Kataloglar', 'Catalogs')}
                  <ChevronDown className="w-3 h-3" />
                </DropdownMenuTrigger>
                <DropdownMenuContent className="bg-vav-bg-paper border-vav-border min-w-[200px]">
                  {catalogs.length > 0 ? (
                    catalogs.map((catalog, index) => (
                      <DropdownMenuItem key={index} asChild>
                        <a 
                          href={catalog.pdf_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-vav-fg hover:text-vav-gold cursor-pointer flex items-center gap-2"
                        >
                          <FileText className="w-4 h-4" />
                          <span>{getLocalizedField(catalog, 'name')}</span>
                        </a>
                      </DropdownMenuItem>
                    ))
                  ) : (
                    <DropdownMenuItem disabled className="text-vav-fg-muted">
                      {t('Katalog bulunamadı', 'No catalogs found')}
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>

              <NavLink to="/about">{t('Hakkımızda', 'About Us')}</NavLink>
              <NavLink to="/contact">{t('İletişim', 'Contact')}</NavLink>
            </div>

            {/* Right Side Actions */}
            <div className="flex items-center gap-3">
              {/* Language Switcher */}
              <LanguageSwitcher />

              {/* Shop Button - External */}
              <motion.a
                href="https://www.taktikstore.com"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="shop-external-btn"
                className="hidden sm:flex items-center gap-2 px-4 py-2 bg-vav-gold text-black font-mono text-xs uppercase tracking-wider rounded hover:bg-vav-gold/90 transition-colors"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <ShoppingBag className="w-4 h-4" />
                {t('Alışveriş', 'Shop')}
              </motion.a>

              {/* Admin Link - Only for admins, no regular user menu */}
              {isAuthenticated && user?.role === 'admin' && (
                <Link 
                  to="/admin"
                  className="px-3 py-1.5 text-xs font-mono uppercase tracking-wider text-vav-gold border border-vav-gold rounded hover:bg-vav-gold hover:text-black transition-colors"
                >
                  Admin
                </Link>
              )}

              {/* Mobile Menu Toggle */}
              <motion.button
                className="lg:hidden p-2 text-vav-fg-muted hover:text-vav-gold transition-colors"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                data-testid="mobile-menu-btn"
                whileTap={{ scale: 0.9 }}
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </motion.button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div 
              className="lg:hidden bg-vav-bg-paper border-t border-vav-border"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <div className="px-4 py-6 space-y-4">
                <a 
                  href="https://www.taktikstore.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block font-mono text-sm uppercase tracking-widest text-vav-gold hover:text-vav-accent-cyan"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {t('Alışveriş Yap', 'Shop Now')} →
                </a>
                
                {/* Categories */}
                <div className="border-t border-vav-border pt-4">
                  <p className="text-xs text-vav-fg-muted uppercase tracking-widest mb-3">
                    {t('Ürün Kategorileri', 'Product Categories')}
                  </p>
                  {categories.map((cat) => (
                    <a 
                      key={cat.category_id}
                      href={cat.external_url || `https://www.taktikstore.com/kategori/${cat.slug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block py-2 text-vav-fg hover:text-vav-gold"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {getLocalizedField(cat, 'name')}
                    </a>
                  ))}
                </div>

                {/* Catalogs */}
                {catalogs.length > 0 && (
                  <div className="border-t border-vav-border pt-4">
                    <p className="text-xs text-vav-fg-muted uppercase tracking-widest mb-3">
                      {t('Kataloglar', 'Catalogs')}
                    </p>
                    {catalogs.map((catalog, index) => (
                      <a 
                        key={index}
                        href={catalog.pdf_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 py-2 text-vav-fg hover:text-vav-gold"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <FileText className="w-4 h-4" />
                        {getLocalizedField(catalog, 'name')}
                      </a>
                    ))}
                  </div>
                )}

                {/* Other Links */}
                <div className="border-t border-vav-border pt-4 space-y-4">
                  <Link 
                    to="/about" 
                    className="block font-mono text-sm uppercase tracking-widest text-vav-fg hover:text-vav-gold"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {t('Hakkımızda', 'About Us')}
                  </Link>
                  <Link 
                    to="/contact" 
                    className="block font-mono text-sm uppercase tracking-widest text-vav-fg hover:text-vav-gold"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {t('İletişim', 'Contact')}
                  </Link>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.nav>
    </>
  );
};

export default Navbar;
